import { useMemo } from "react";
import { MarketingMaterialTableItem } from "shared/types/marketingMaterials";
import {
  audienceFilterOptions,
  Languages,
} from "utils/helpers.salesEnablement";
import ToolbarTable from "shared/components/toolbarTable/ToolbarTable";
import { useDataListURLGlobalFilter } from "shared/components/dataListURL/useDataListURLGlobalFilter";
import { DataListTagsURL } from "shared/components/dataListURL/DataListTagsURL";
import DataListURLTable, {
  ITableColumnURL,
} from "shared/components/dataListURL/DataListURLTable";
import { SearchOutlined } from "@ant-design/icons";
import NameCardCell from "shared/components/NameCardCell";
import { deliveryMethods } from "screens/designStudio/utils";
import Tags from "screens/designStudio/shared/Tags";
import { uniq, upperCase } from "lodash";
import { SearchFilterDropdown } from "shared/components/SearchFilterDropdown";
import {
  customizableMap,
  fields,
} from "screens/designStudio/templates/fileDrawer/FormFields.schema";
import { getFiltersFromData } from "utils/helpers.table";
import { Language } from "shared/types/salesEnablement";
import EmptyMarketingMaterials from "../../EmptyMarketingMaterials";
import { marketingMaterialFieldsKeys } from "../../marketingMaterialsTable/fields";
import { useWindowSize } from "shared/hooks/useWindowSize";
import { TableActionsContextProvider } from "shared/components/HoverActionsVirtualTable/TableActionsContext";

type Props = {
  data: MarketingMaterialTableItem[];
  originalData: MarketingMaterialTableItem[];
  isLoading: boolean;
};
const MarketingMaterialList = ({ data, originalData, isLoading }: Props) => {
  const { windowInnerHeight } = useWindowSize();
  const { globalFilter, setGlobalFilter } = useDataListURLGlobalFilter();
  const getFilters = getFiltersFromData(data);

  const locations = useMemo(
    () => uniq(originalData.map(item => item.locations).flat()).sort(),
    [originalData],
  );
  const columns: ITableColumnURL<
    keyof MarketingMaterialTableItem,
    MarketingMaterialTableItem
  >[] = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 280,
      filterDropdown: SearchFilterDropdown,
      filterIcon: <SearchOutlined />,
      render: (_, materialItem) => {
        return (
          <NameCardCell
            name={materialItem.name}
            thumbnail={materialItem.templateThumbnail}
          />
        );
      },
    },
    {
      key: "templateDeliveryMethods",
      title: "Delivery Method",
      width: 200,
      dataIndex: "deliveryMethods",
      filters: deliveryMethods.map(method => ({
        text: method.label,
        value: method.value,
      })),
      render: (_, material) => {
        return (
          <Tags
            tags={
              uniq(material?.templateDeliveryMethods)?.map(item => ({
                id: item + material.id,
                name:
                  deliveryMethods.find(opt => opt.value === item)?.label ||
                  item,
              })) ?? []
            }
          />
        );
      },
    },
    {
      title: "States",
      dataIndex: "locations",
      key: "locations",
      width: 200,
      filters: locations.map(state => ({
        text: state,
        value: state,
      })),
      render: (_, material) => (
        <Tags
          tags={material.locations?.map(state => ({
            id: state,
            name: state,
          }))}
        />
      ),
    },
    {
      key: "templateCustomizable",
      title: "Customizable",
      dataIndex: "templateCustomizable",
      width: 200,
      filters: fields.customizable.options.map(item => ({
        text: item.label,
        value: item.value,
      })),
      render: (_, material) =>
        material.templateCustomizable
          ? customizableMap.customizable
          : customizableMap.nonCustomizable,
    },
    {
      title: "Audience",
      dataIndex: "audience",
      key: "templateAudience",
      width: 200,
      filters: audienceFilterOptions.map(option => ({
        text: option.label,
        value: option.value,
      })),
      render: (_, material) => (
        <Tags
          tags={material.templateAudience?.map(item => ({
            id: item,
            name:
              audienceFilterOptions.find(opt => opt.value === item)?.label ||
              item,
          }))}
        />
      ),
    },
    {
      title: "Language",
      dataIndex: "language",
      key: "language",
      width: 150,
      render: upperCase,
      filters: getFilters("language", {
        textMapper: (value: string) => Languages[value as Language].label,
        sorted: true,
      }),
    },
  ];
  return (
    <>
      <ToolbarTable
        layout={"table"}
        titleTooltip="Search"
        searchPlaceholder="Search"
        toolbarContents={{}}
        onSearch={setGlobalFilter}
        searchValue={globalFilter ?? undefined}
        disableSearch={false}
        previewFormat={false}
        includeFilterOnTableLayout={true}
      />
      <DataListTagsURL<
        keyof MarketingMaterialTableItem,
        MarketingMaterialTableItem
      >
        data={data}
        originalData={originalData}
        showSelectAll={false}
      />
      <TableActionsContextProvider<MarketingMaterialTableItem>
        renderContextActions={() => {
          return null;
        }}
        renderHoverActions={() => {
          return null;
        }}
      >
        <DataListURLTable<
          keyof MarketingMaterialTableItem,
          MarketingMaterialTableItem
        >
          loading={isLoading}
          dataSource={data}
          columns={columns}
          pagination={false}
          locale={{
            emptyText: <EmptyMarketingMaterials />,
          }}
          size="small"
          scroll={{ y: windowInnerHeight - 245 }}
          type="hoverActionsVirtualTable"
          fieldKeys={marketingMaterialFieldsKeys}
        />
      </TableActionsContextProvider>
    </>
  );
};

export default MarketingMaterialList;
